import { useTypedSelector, RootState } from "src/shared/store/store"
import { useMemo } from "react"

export const usePermission = () => {
  const permissions = useTypedSelector((state: RootState) => state.authentication.permissions)

  const permissionIndex = useMemo(() => {
    const exact = new Set<string>()
    const wildcardByFirst = new Map<string, string[][]>()
    const wildcardAll: string[][] = []

    for (const p of permissions ?? []) {
      const raw = p.permission?.trim()
      if (!raw) continue

      const parts = raw.split(":")
      const starIdx = parts.indexOf("*")

      if (starIdx === -1) {
        exact.add(raw)
        continue
      }

      const prefix = parts.slice(0, starIdx) // '*' matches the rest
      if (prefix.length === 0) {
        wildcardAll.push(prefix)
        continue
      }

      const key = prefix[0]
      const list = wildcardByFirst.get(key) ?? []
      list.push(prefix)
      wildcardByFirst.set(key, list)
    }

    const matches = (required: string): boolean => {
      if (!required?.trim()) return false
      if (exact.has(required)) return true

      const reqParts = required.split(":")

      // global wildcard like "*" matches everything
      if (wildcardAll.length) return true

      const candidates = wildcardByFirst.get(reqParts[0] ?? "") ?? []
      outer: for (const prefix of candidates) {
        for (let i = 0; i < prefix.length; i++) {
          if (prefix[i] !== reqParts[i]) continue outer
        }
        return true
      }
      return false
    }

    return { matches }
  }, [permissions])

  const hasPermission = (requirePermission: string): boolean => {
    if (!permissions?.length) return false
    return permissionIndex.matches(requirePermission)
  }

  const hasAllPermissions = (requirePermissions: string[]): boolean => {
    if (!permissions?.length) return false
    if (!Array.isArray(requirePermissions) || requirePermissions.length === 0) return false

    for (const req of requirePermissions) {
      if (!permissionIndex.matches(req)) return false
    }
    return true
  }

  const getPermissions = (requirePermission: string) => {
    if (!permissions?.length) return null

    const requiredParts = requirePermission.split(":")

    return permissions?.find((perm) => {
      const permParts = perm.permission.split(":")

      // Handle cases where permission parts don't match in length
      if (requiredParts.length !== permParts.length) {
        if (permParts[permParts.length - 1] === "*") {
          while (requiredParts.length < permParts.length) {
            requiredParts.push("")
          }
        } else {
          return false
        }
      }

      // Compare each part
      for (let i = 0; i < permParts.length; i++) {
        if (permParts[i] === "*") {
          return true
        }
        if (permParts[i] !== requiredParts[i]) {
          return false
        }
      }

      return true
    })
  }

  return { hasPermission, hasAllPermissions, getPermissions }
}
// End of Selection
